import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, forwardRef } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { cva } from '../utils/class-variance.util';
import { InputSize } from '../models/input.model';

const inputVariants = cva({
  base: [
    'w-full rounded-xl border px-4 py-3',
    'transition-all duration-300 ease-out',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-40',
    // Auth-like dark input surfaces with consistent contrast
    'placeholder:text-slate-200',
    'backdrop-blur-md bg-slate-600/65',
    'border-slate-300/35',
    'text-slate-50',
    'shadow-md shadow-slate-950/20',
    'hover:bg-slate-500/72',
    'hover:border-slate-200/45',
    'focus:bg-slate-500/80',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'focus:border-primary-400/50 focus:ring-primary-500/50',
        'focus:shadow-xl focus:shadow-primary-500/25',
      ].join(' '),
      error: [
        'border-red-400/50 bg-red-50/20 dark:bg-red-900/10',
        'focus:border-red-500/60 focus:ring-red-500/50',
        'focus:shadow-xl focus:shadow-red-500/25',
        'text-red-900 dark:text-red-100',
      ].join(' '),
      success: [
        'border-cyan-400/50 bg-cyan-50/20 dark:bg-cyan-900/10',
        'focus:border-cyan-500/60 focus:ring-cyan-500/50',
        'focus:shadow-xl focus:shadow-cyan-500/25',
        'text-cyan-900 dark:text-cyan-100',
      ].join(' '),
    },
    size: {
      sm: 'h-9 text-sm',
      md: 'h-11 text-base',
      lg: 'h-12 text-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

@Component({
  selector: 'ui-input',
  standalone: true,
  imports: [ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputGlassComponent),
      multi: true,
    },
  ],
  templateUrl: './input-glass.component.html',
  host: {
    class: 'block w-full',
  },
})
export class InputGlassComponent implements ControlValueAccessor, AfterViewInit {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  @Input() size: InputSize = 'md';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() helpText: string = '';
  @Input() errorMessage: string = '';
  @Input() successMessage: string = '';
  @Input() iconLeft: boolean = false;
  @Input() iconRight: boolean = false;
  @Input() customClass: string = '';
  @Input() appearance: 'fill' | 'outline' = 'fill'; // Material compatibility
  @Input() floatLabel: 'auto' | 'always' | 'never' = 'never';
  @Input() hint: string = ''; // Alternative to helpText
  @Input() maxLength: number | null = null;
  @Input() minLength: number | null = null;
  @Input() pattern: string = '';
  @Input() autocomplete: string = '';
  @Input() clearable: boolean = false;
  @Input() showCounter: boolean = false;

  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  protected inputId = `input-${Math.random().toString(36).substring(2, 11)}`;
  protected value: string = '';

  // ControlValueAccessor
  private onChange = (_value: string) => {};
  private onTouched = () => {};

  protected computedVariant(): 'default' | 'error' | 'success' {
    if (this.errorMessage) return 'error';
    if (this.successMessage) return 'success';
    return 'default';
  }

  // Use a method instead of a signal-backed computed to ensure template
  // updates immediately in tests and environments where signal reactivity
  // may not be observed during synchronous change detection.
  protected getComputedClasses(): string {
    const baseClasses = inputVariants({
      variant: this.computedVariant(),
      size: this.size,
    });
    return `ui-input-field ${baseClasses}${this.customClass ? ` ${this.customClass}` : ''}`;
  }

  protected characterCount(): number {
    return this.value?.length || 0;
  }

  protected showHint(): boolean {
    return !!(this.hint || this.helpText) && !this.errorMessage;
  }

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const input = this.host.nativeElement.querySelector('input');
    if (!input) {
      return;
    }
    this.enforceInputSurfaceStyles(input);
    // Chrome can repaint autofill async after render.
    setTimeout(() => this.enforceInputSurfaceStyles(input), 0);
    setTimeout(() => this.enforceInputSurfaceStyles(input), 120);
  }

  private enforceInputSurfaceStyles(input: HTMLInputElement): void {
    const fill = 'rgba(77, 91, 118, 0.92)';
    const text = '#f5f8ff';
    const border = 'rgba(173, 192, 230, 0.35)';
    const shadow = '0 0 0 1000px rgba(77, 91, 118, 0.92) inset, 0 1px 8px rgba(2, 6, 23, 0.28)';

    input.style.setProperty('background-color', fill, 'important');
    input.style.setProperty('background-image', 'none', 'important');
    input.style.setProperty('-webkit-text-fill-color', text, 'important');
    input.style.setProperty('color', text, 'important');
    input.style.setProperty('-webkit-box-shadow', shadow, 'important');
    input.style.setProperty('box-shadow', shadow, 'important');
    input.style.setProperty('border-color', border, 'important');
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.enforceInputSurfaceStyles(target);
    this.value = target.value;
    this.onChange(this.value);
  }

  protected onFocus(event: FocusEvent): void {
    this.enforceInputSurfaceStyles(event.target as HTMLInputElement);
    this.focus.emit();
  }

  protected onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  protected clearInput(): void {
    this.value = '';
    this.onChange(this.value);
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

/**
 * Usage Examples:
 *
 * Basic glassmorphism input:
 * <ui-input label="Email" placeholder="Enter your email" type="email"></ui-input>
 *
 * Material-style floating label:
 * <ui-input label="Name" floatLabel="auto"></ui-input>
 *
 * With form control and validation:
 * <ui-input
 *   label="Password"
 *   type="password"
 *   [formControl]="passwordControl"
 *   [errorMessage]="passwordControl.errors?.['required'] ? 'Password is required' : ''"
 *   [showCounter]="true"
 *   [maxLength]="50"
 * ></ui-input>
 *
 * With clearable and character counter:
 * <ui-input
 *   label="Description"
 *   [clearable]="true"
 *   [showCounter]="true"
 *   [maxLength]="200"
 *   hint="Tell us about yourself"
 * ></ui-input>
 *
 * With icons:
 * <ui-input label="Search" [iconLeft]="true">
 *   <svg slot="icon-left" class="w-4 h-4 text-gray-400">...</svg>
 * </ui-input>
 *
 * Different appearances:
 * <ui-input appearance="outline" label="Outlined"></ui-input>
 * <ui-input appearance="fill" label="Filled"></ui-input>
 *
 * Different sizes:
 * <ui-input size="sm" label="Small"></ui-input>
 * <ui-input size="lg" label="Large"></ui-input>
 */
